import { useCallback, useEffect, useState } from "react"
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore"
import { auth, db } from "lib/firebase"
import { normalizeFlight } from "utils/flightLogic"

const COLLECTION = "flights"

export function useFlights() {
  const [flights, setFlights] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, COLLECTION),
      (snapshot) => {
        setFlights(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      }
    )
    return unsubscribe
  }, [])

  const clearError = useCallback(() => setError(null), [])

  // These deliberately do not return their promises and must never be awaited
  // by callers. Offline, addDoc/updateDoc do not settle until the server
  // acknowledges — but Firestore has already applied the write locally and
  // onSnapshot has already fired, so awaiting would hang the UI on a write
  // that actually succeeded.
  const addFlight = useCallback((input) => {
    addDoc(collection(db, COLLECTION), {
      ...normalizeFlight(input),
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || null,
    }).catch((err) => setError(err.message))
  }, [])

  const updateFlight = useCallback((id, input) => {
    updateDoc(doc(db, COLLECTION, id), normalizeFlight(input)).catch((err) =>
      setError(err.message)
    )
  }, [])

  const deleteFlight = useCallback((id) => {
    deleteDoc(doc(db, COLLECTION, id)).catch((err) => setError(err.message))
  }, [])

  return {
    flights,
    loading,
    error,
    clearError,
    addFlight,
    updateFlight,
    deleteFlight,
  }
}
